import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { usePageState } from "@/hooks/usePageState";
import { PageError } from "@/features/auth/components/PageError";
import { PageAccessError } from "@/lib/queries";
import { PartnerProductionQueuePage } from "@/features/marketplace/partner/pages/PartnerProductionQueuePage";

const partnerOrdersSearchSchema = z.object({
  /** Exact order id to pre-fill the production tracker filter (deep link). */
  orderId: z.string().optional().catch(undefined),
  /** Order script row to expand and scroll into view (requires orderId). */
  orderScriptId: z.string().optional().catch(undefined),
});

export type PartnerOrdersSearch = z.infer<typeof partnerOrdersSearchSchema>;

export const Route = createFileRoute("/_authenticated/marketplace/partner/")({
  validateSearch: partnerOrdersSearchSchema,
  component: MarketplacePartnerIndexRoute,
});

function MarketplacePartnerIndexRoute() {
  const { t } = useTranslation();
  useDocumentTitle(t("marketplace.partner.title", "Production orders"));

  const { orderId: initialOrderId, orderScriptId: initialOrderScriptId } = Route.useSearch();
  const { isLoading, error } = usePageState("marketplace.partner");

  if (isLoading) return null;
  if (error) return <PageError is403={error instanceof PageAccessError} />;
  return (
    <PartnerProductionQueuePage
      initialOrderId={initialOrderId}
      initialOrderScriptId={initialOrderScriptId}
    />
  );
}
